import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import { HttpClient } from 'support/api/api-types';

export interface ErrorLike {
  message?: string;
  stack?: string;
  code?: string | number;
  type?: string;
  target?: unknown;
  toString?: () => string;
}
/** Promise action for custom promise middleware */
export interface PromiseAction<T = unknown> {
  type: string;
  promise: Promise<T> | ((httpClient: HttpClient) => Promise<T>);
  meta?: unknown;
}

/** Combined dispatch type supporting thunks and promise middleware */
export interface AppDispatch<State = unknown> {
  // Promise action overload - must come first
  <T>(action: PromiseAction<T>): Promise<T>;
  // Thunk overload
  <R>(thunk: ThunkAction<R, State, unknown, AnyAction>): R;
  // Plain action overload
  <A extends AnyAction>(action: A): A;
}

export interface ChannelInfo {
  resourceId: string;
  name: string;
  logoSmallUrl: string;
  logoMediumUrl: string;
  logoLargeUrl: string;
  logoFixedSmallUrl: string;
  logoFixedMediumUrl: string;
  logoFixedLargeUrl: string;
}

export type ChannelMap = Record<string, ChannelInfo>;

export type ChannelKey =
  | 'CBSE'
  | 'CBSSN'
  | 'PARAMOUNTPLUS'
  | 'CBSSHQ'
  | 'CBSSGOLAZO'
  | 'CBSSPORTS'
  | 'NFLCHANNEL'
  | 'CBSNEWS'
  | 'CBSNEWS247'
  | 'CBSNEWSBALTIMORE'
  | 'CBSNEWSBOSTON'
  | 'CBSNEWSCHICAGO'
  | 'CBSNEWSCOLORADO'
  | 'CBSNEWSDETROIT'
  | 'CBSNEWSLOSANGELES'
  | 'CBSNEWSMIAMI'
  | 'CBSNEWSMINNESOTA'
  | 'CBSNEWSNEWYORK'
  | 'CBSNEWSPHILADELPHIA'
  | 'CBSNEWSPITTSBURGH'
  | 'CBSNEWSSACRAMENTO'
  | 'CBSNEWSSANFRANCISCO'
  | 'CBSNEWSTEXAS'
  | 'CBSCHAMPIONSLEAGUE'
  | 'CARCHASE'
  | 'ENTERTAINMENTTONIGHT'
  | 'INSIDEEDITION'
  | 'CBS48HOURS'
  | 'CBS60MINUTES';

/** Strictly typed channel map with known keys */
export type Network = Record<ChannelKey, ChannelInfo>;

export const Providers = {
  ADOBE: 'adobe',
  PARAMOUNT_PLUS: 'paramountplus',
} as const;
/** Raw API response for autoplay video object (before parsing by parseVideo) */
export interface AutoPlayObjectType {
  promo: unknown | null;
  featured: boolean;
  id: string;
  video: {
    orientation?: string;
    sources: {
      dash: {
        url: string | null;
        urlNoAd: string | null;
      };
      hls: {
        url: string | null;
        urlNoAd: string | null;
      };
    };
    identifiers: {
      id: string;
      slug: string;
      viacomContentId: string | null;
      trackingId: string;
    };
    relatedObject: unknown | null;
    about: {
      duration?: number;
      images?: {
        baseImage16X9: string | null;
        baseImage16X5: string | null;
      };
      prefix: string | null;
      description?: string;
      publishedOn?: number;
      segmentTitle: string | null;
      shortTitle?: string;
      title: string;
    };
    flags: {
      suppressPreroll?: boolean;
      isPlaybackBrowser?: boolean;
      isAppDrawer?: boolean;
      isAppScoreboard?: boolean;
      useDai?: boolean;
    };
    network: ChannelKey | null;
    analytics?: {
      neilsonGenre: string | null;
    };
    schedule: {
      preEndDate: number | null;
      preStartDate: number | null;
      videoStartDate: number | null;
      postEndDate: number | null;
      videoEndDate: number | null;
      apiStartDate: number | null;
      apiEndDate: number | null;
      postStartDate: number | null;
    };
    ads: {
      dai?: {
        daiApiKey: string | null;
        daiIu: string | null;
        daiCsid: string | null;
        daiApto: number | null;
        daiOv: string | null;
        daiAssetKey: string | null;
        daiOt: string | null;
      };
      vtag: string | null;
    };
    location?: boolean;
    playback: 'openHLS' | 'syncbak' | 'hls_encryption' | 'fullDRM' | string;
    event: {
      preEvent: string | null;
      pregame: string | null;
      postgame: string | null;
      frequency?: number;
      postEvent: string | null;
      status: 'active' | 'inactive' | string;
    };
    properties: {
      gameId: string | null;
      playlist: string | null;
      programInfo?: {
        source: string;
        frequency: number;
      };
      gameStartTime: string | null;
      league: string | null;
      eventType: string | null;
      type: 'vod' | 'live' | 'live-linear' | string;
      tags: unknown | null;
      tagSlugs?: string[];
    };
    authenticationDetails: {
      adobe: unknown | null;
      paramountplus: unknown | null;
    };
    authentication: Provider[];
    series?: {
      id: string | null;
      season: string | null;
      episode: string | null;
    };
  };
  order: number;
  deeplink: string | null;
  dma: string | null;
  dmaGroup: string[];
  division?: string[];
}

/** Promo object type for promotional items in row data */
export interface PromoType {
  description: string | null;
  images: {
    baseImage16X9: string | null;
    baseImage16X5: string | null;
  } | null;
  title: string;
  endDate: number | null;
  startDate: number | null;
  tag?: {
    id: number;
    key: string;
    type?: {
      name: string;
    };
  } | null;
}

/** Row data item from kitchen sink API response */
export interface RowDataItem {
  id: string;
  promo: PromoType | null;
  video: AutoPlayObjectType['video'] | null;
  featured: boolean;
  deeplink: string | null;
  order: number;
}

export type Provider = typeof Providers[keyof typeof Providers];

export type Platform =
  | 'unknown'
  | 'samsung-orsay'
  | 'samsung-tizen'
  | 'vizio'
  | 'tcl'
  | 'lg-webos'
  | 'lg-netcast'
  | 'sony'
  | 'sagem'
  | 'netgem'
  | 'tivo'
  | 'ps3'
  | 'ps4'
  | 'xboxone'
  | 'windows-desktop'
  | 'androidtv'
  | 'firetv';

export type PageType = 'sport' | 'league' | 'market' | 'show' | 'custom' | null;

export interface TypeIdKeys {
  type: string;
  id: string;
}

export interface Video {
  id: string;
  network?: ChannelKey;
  authTypes?: Provider[];
}
export type HttpPayloadBaseType = boolean | string | number | undefined | null;
export type HttpPayloadBaseObject = Record<
  string,
  HttpPayloadBaseType | HttpPayloadBaseType | HttpPayloadBaseType[]
>;
export type HttpPayloadObject = Record<
  string,
  HttpPayloadBaseType | HttpPayloadBaseObject
>;

export interface GridItem {
  id: string;
  title: string;
  name: string;
}

export interface ScoringFixtureTeam {
  mediumName: string;
  nickName: string;
}

export interface ScoringFixtureGame {
  gameStatusDesc: string;
  gameId: string;
  awayTeamId: string;
  awayTeam: ScoringFixtureTeam;
  awayTeamScore: string;
  homeTeamId: string;
  homeTeam: ScoringFixtureTeam;
  homeTeamScore: string;
  scheduledTime: string;
  leagueId: string;
  leagueName: string | null;
  gameTypeDetail?: string;
}

export interface ApiError {
  isConnectionError: () => boolean;
  statusCode?: number;
  response?: {
    status?: number;
  };
}

/** Raw kitchen sink fixture module param */
export interface KitchenSinkFixtureModuleParam {
  value: string;
  name: string;
}

/** Raw kitchen sink fixture module */
export interface KitchenSinkFixtureModule {
  params: KitchenSinkFixtureModuleParam[];
  name: string;
  category: string;
  enabled: boolean;
}

/** Raw kitchen sink fixture order item */
export interface KitchenSinkFixtureOrderItem {
  type: string;
  slug: string | null;
  id: string;
  title: string;
  description: string | null;
  url: string;
  layout: string;
  token: string;
}

/** Raw kitchen sink fixture row item - loose typing for complex nested structure */
export interface KitchenSinkFixtureRowItem {
  promo: unknown;
  featured: boolean;
  id: string;
  video: Record<string, unknown>;
  order?: number;
  deeplink: unknown;
}

/** Raw kitchen sink fixture config */
export interface KitchenSinkFixtureConfig {
  modules: KitchenSinkFixtureModule[];
  classes: unknown[];
}

/** Raw kitchen sink fixture page data */
export interface KitchenSinkFixturePage {
  version: number;
  menuId: number;
  autoplay: string;
  config: KitchenSinkFixtureConfig;
  order: KitchenSinkFixtureOrderItem[];
  rows: Record<string, KitchenSinkFixtureRowItem[]>;
  providers?: Record<string, unknown>;
  networks?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  globalSettings?: Record<string, unknown>;
  playback?: Record<string, unknown>;
  hero?: Record<string, unknown>;
  menu?: unknown[];
  tag_rows?: Record<string, unknown>;
  autoplayObject?: Record<string, unknown>;
}

/** Raw kitchen sink fixture - top level */
export interface KitchenSinkFixture {
  page: KitchenSinkFixturePage;
}
